import { displayUnitOf } from "@heraion/shared";
import { useMemo } from "react";

import { ExercisePickerMount } from "../ExercisePickerMount";
import {
  PERSISTED_RESULTS_KEY,
  asyncStorageResourceCache,
  createSessionResultsCache,
  createStaleResultsReader,
  persistedResultsOf,
  readPersistedResults,
  resolveTheme,
  type PersistedResults,
  type PersistResults,
  type ProgressDeps,
  type UseSessionResultsOptions,
} from "@heraion/ui";
import {
  EMPTY_PAIN_MAP,
  Profile,
  type PainMap,
} from "@heraion/shared";

import { apiFetch } from "../api";
import { exerciseCatalog } from "../logger/exercisesCache";
import { lsCache } from "../lsCache";
import { useShellProfile } from "../ThemeProfileBridge";
import type { UseProfileDoc } from "../useProfileDoc";
import { useThemePalette } from "../theme";

// One single-flight session-results cache shared by every SELF surface wired
// through this hook: the Progress panels warm it, and the Goals editor
// (TargetsEditor) paints its reference numbers straight from it (getCached is
// synchronous) instead of after a spinner. Reusing the SAME instance avoids a
// cold-paint regression and two caches that could diverge on save. Mirror of
// mobile's apps/mobile/src/progress/progressDeps.ts. The Home (PlanScreen: the
// meter, the composer, the widgets) reads it too since CN-self-results-cache.
const resultsCache = createSessionResultsCache();

// RW-18: the Logger's `invalidateSessionResults` host dep — drops this SAME
// cache after a save so Progress refetches fresh, matching mobile's
// apps/mobile/src/progress/sessionResultsCache.ts's invalidate. Exported as
// a plain function (not a hook) because useSaveWorkout's caller has no
// component to hook from — same reasoning as mobile's split-out module.
export const invalidateSelfSessionResults = (): void => resultsCache.invalidate();

// Cold-boot disk SWR, localStorage twin of mobile's AsyncStorage
// resultsPersist (apps/mobile/src/progress/progressDeps.ts), under the same
// shared key: `lsCache` is a drop-in for `asyncStorageResourceCache`
// (both `{read,write}` over a single JSON blob).
// #469: `{results, rawCount}` under a key of its own (PERSISTED_RESULTS_KEY),
// so an older build never meets this shape under the key it reads.
const resultsPersist = lsCache<PersistedResults>(PERSISTED_RESULTS_KEY);

// Module single-flight for the stale disk read so the panels that mount
// together share ONE round-trip instead of each hitting disk (#397: kept for
// one account, see createStaleResultsReader).
const staleResults = createStaleResultsReader(() =>
  Promise.all([resultsPersist.read(), exerciseCatalog.loadCached()]).then(
    ([kept, catalog]) => readPersistedResults(kept, catalog),
  ),
);
const readStaleResults = staleResults.read;

const persistResults: PersistResults = (results, scope, rawCount) => {
  // A fresh fetch supersedes any stale disk snapshot from this run.
  staleResults.reset();
  void resultsPersist.write(persistedResultsOf(results, rawCount), scope);
};

// RW-18: the Logger's `sessionResultsDeps` host dep (hostDeps.ts —
// "useSessionResults' own deps bundle") — the SAME resultsCache instance
// above, so a save invalidates the identical cache the in-workout meter and
// the Progress panels read, plus the cold-boot localStorage SWR above
// (mirrors mobile's apps/mobile/src/progress/progressDeps.ts's plain
// (non-hook) export).
export const selfSessionResultsDeps: UseSessionResultsOptions = {
  apiFetch,
  catalog: exerciseCatalog,
  cache: resultsCache,
  readStale: readStaleResults,
  persistResults,
};

// The SELF ProgressDeps — my own training over the unscoped apiFetch, not an
// athlete I coach (that lives in AthleteDetailScreen with a per-sub scoped cache).
// The minimal fields the shared Tier-1 panels + TargetsEditor consume;
// ProgressScreen layers its PG-1 extras (figure / targets) on top of this base.
export function useSelfProgressDeps(): ProgressDeps {
  const { doc } = useShellProfile();
  return useSelfProgressDepsFor(doc);
}

/** The same deps from a profile doc the caller already holds. */
export function useSelfProgressDepsFor(doc: UseProfileDoc["doc"]): ProgressDeps {
  const { dark, family } = useThemePalette();
  const units = displayUnitOf(doc);
  const weekStartDay = doc?.["weekStartDay"] as number | undefined;
  // BodyScreen's PainMapPicker (lifted 2026-08-16) writes painMap into this
  // SAME profile doc via savePatch — read it back the way BodyScreen.tsx:45
  // does, mirroring mobile's `profile.painMap ?? EMPTY_PAIN_MAP`.
  const painMap =
    (doc?.["painMap"] as PainMap | undefined) ?? EMPTY_PAIN_MAP;
  // AP-9: the gap proposal lists what is doable at the default place.
  const rawPlace = doc?.["defaultLocation"];
  const rawDeclared = doc?.["locationUnavailable"];
  const trainingPlace = useMemo(() => {
    const place = Profile.shape.defaultLocation.safeParse(rawPlace);
    const declared = Profile.shape.locationUnavailable.safeParse(rawDeclared);
    return {
      place: place.success ? (place.data ?? null) : null,
      unavailable: declared.success ? declared.data : undefined,
    };
  }, [rawPlace, rawDeclared]);
  return useMemo<ProgressDeps>(
    () => ({
      apiFetch,
      catalog: exerciseCatalog,
      // PG-24 — the same mount TimersScreen already uses, so the "compare
      // with" control exists on web too. ⛔ Without this the button would be
      // absent on web only, which is the silent lift gap `rnw-strangler §8`
      // exists to stop — and the mount was already here, so there was nothing
      // to park.
      ExercisePickerMount,
      theme: resolveTheme(dark, family),
      units,
      painMap,
      resultsCache,
      readStaleResults,
      persistResults,
      // #397: each widget's disk copy, localStorage under mobile's keys.
      diskCache: asyncStorageResourceCache,
      isDark: dark,
      weekStartDay,
      trainingPlace,
    }),
    [dark, family, units, weekStartDay, painMap, trainingPlace],
  );
}
